/**
 * LoadingSpinner - Componente unificado de carga para toda la aplicación
 *
 * Este es el ÚNICO componente de loading que debe usarse en toda la app.
 * Reemplaza a: Spinner, PageLoader, LoadingState, TableLoadingState, etc.
 *
 * IMPORTANTE: Siempre usa colores gray/negro - NUNCA blue/primary
 *
 * @example
 * // Spinner simple inline
 * <LoadingSpinner size="sm" />
 *
 * // Spinner con texto
 * <LoadingSpinner size="md" text="Cargando datos..." />
 *
 * // Spinner centrado en página completa
 * <LoadingSpinner size="lg" text="Cargando..." fullScreen />
 *
 * // Spinner para tablas
 * <LoadingSpinner size="md" text="Cargando tabla..." centered />
 */

import React from 'react';

export interface LoadingSpinnerProps {
  /** Tamaño del spinner */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Texto de carga opcional */
  text?: string;
  /** Centrar en el contenedor */
  centered?: boolean;
  /** Ocupar toda la pantalla */
  fullScreen?: boolean;
  /** Clase CSS adicional */
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  text,
  centered = false,
  fullScreen = false,
  className = '',
}) => {
  // Tamaños del spinner
  const spinnerSizes = {
    xs: 'h-3 w-3 border-2',
    sm: 'h-4 w-4 border-2',
    md: 'h-8 w-8 border-[3px]',
    lg: 'h-12 w-12 border-4',
    xl: 'h-16 w-16 border-4',
  };

  // Tamaños del texto
  const textSizes = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
  };

  const spinnerClasses = `
    animate-spin
    rounded-full
    border-gray-300
    dark:border-gray-600
    border-t-gray-900
    dark:border-t-gray-100
    ${spinnerSizes[size]}
  `.trim().replace(/\s+/g, ' ');

  const textClasses = `
    ${textSizes[size]}
    text-gray-600
    dark:text-gray-400
    font-medium
    mt-3
  `.trim().replace(/\s+/g, ' ');

  // Contenedor base
  const baseContainer = (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <div className={spinnerClasses} role="status" aria-label={text || 'Cargando...'}>
        <span className="sr-only">{text || 'Cargando...'}</span>
      </div>
      {text && <p className={textClasses}>{text}</p>}
    </div>
  );

  // Full screen
  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white dark:bg-gray-900 flex items-center justify-center z-50">
        {baseContainer}
      </div>
    );
  }

  // Centered (para usar dentro de containers)
  if (centered) {
    return (
      <div className="flex items-center justify-center py-12 w-full">
        {baseContainer}
      </div>
    );
  }

  // Default: inline
  return baseContainer;
};

LoadingSpinner.displayName = 'LoadingSpinner';

export default LoadingSpinner;
